import { api } from './client';
import { longRequest } from './requestTimeout';
import type { Domain, OrgDomainsListResponse } from '@/types';

export interface ConnectCustomDomainPayload {
  domain: string;
}

export interface PurchaseDomainPayload {
  domain: string;
}

export interface CheckAvailabilityPayload {
  domainName: string;
  zones: string[];
}

export interface ToggleAutoRenewalPayload {
  autoRenewal: boolean;
}

export interface DomainAvailabilityResult {
  domain: string;
  available: boolean;
  priceCents?: number;
  /**
   * WEB-1639: регистратор не назвал цену. Тогда priceCents в ответе НЕТ (а не
   * ноль, как было раньше), и покупку предлагать нельзя — сервер откажет 503
   * DOMAIN_PRICE_UNAVAILABLE. Общее правило чтения — isPurchasePriceUnavailable
   * в components/domains/shared/domainMeta.ts.
   */
  priceUnavailable?: boolean;
  currency?: string;
  error?: string;
}

export interface RenewBulkPayload {
  ids: string[];
}

export interface RenewBulkResult {
  domainId: string;
  domainName: string;
  paidCents?: number;
  newExpirationDate?: string;
  error?: string;
  /**
   * WEB-2957 — машинный код отказа (ключ реестра сервера) рядом с прежним
   * `error`. Кабинет переводит по нему: `error` бывает русским.
   */
  code?: string;
}

export interface RenewBulkResponse {
  succeeded: RenewBulkResult[];
  failed: RenewBulkResult[];
}

/**
 * Ответ «Продлить» одного домена — одна форма у ручки проекта и у ручки
 * пула организации (WEB-3821: сервер отвечает той же частью продления).
 */
export interface RenewDomainApiResponse {
  success: boolean;
  newExpirationDate?: string;
  totalCost?: number;
  error?: string;
  /** WEB-2957 — код отказа реестра рядом с `error` (перевод — по нему). */
  code?: string;
  /**
   * WEB-1636 — сервер сам говорит, что здесь поможет ОСОЗНАННЫЙ повтор
   * новым списанием: ключ цикла держит живая проводка (деньги ушли,
   * регистратор не отвечал), и домен ещё не помечен «нужна ручная
   * сверка». Признак машинный намеренно — узнавать состояние по тексту
   * отказа значило бы держать на витрине копию серверной формулировки.
   */
  forceRetryAvailable?: boolean;
}

/** Ответ осознанного повтора продления (WEB-1636) — у проекта и у пула организации. */
export interface ForceRenewDomainApiResponse {
  success: boolean;
  newExpirationDate?: string;
  paidCents?: number;
  currency?: string;
  error?: string;
  /** WEB-2957 — код отказа реестра рядом с `error` (перевод — по нему). */
  code?: string;
}

/** Ответ переключателя автопродления: `autoRenewal` — то, что записал сервер. */
export interface ToggleAutoRenewalApiResponse {
  success: boolean;
  autoRenewal?: boolean;
  error?: string;
}

/**
 * WEB-1921 — ответ проверки DNS-записей.
 *
 * Три состояния, а не два: «проверить не удалось» (`check_failed`) НЕ равно
 * «записей нет» (`records_missing`). Путать их нельзя — иначе сетевой сбой
 * гонит человека переделывать исправную зону.
 */
export interface DnsCheckRecord {
  name: string;
  type: string;
  expected: string;
  found: boolean;
  /** Что реально стоит в зоне (пусто — записи нет вовсе). */
  actual: string[];
}

export interface DnsCheckResult {
  status: 'ok' | 'records_missing' | 'check_failed' | string;
  records: DnsCheckRecord[];
}

export interface DnsCheckResponse {
  success: boolean;
  results: Array<DnsCheckResult & { domainId: string; domain: string }>;
}

/** WEB-2489 — ответ перезапуска подключения своего домена. */
export interface RestartDomainConnectionResult {
  success: boolean;
  verificationStatus: string;
  registered: boolean;
}

export const domainsApi = {
  list: (projectId: string) =>
    api.get<{ success: boolean; domains: Domain[] }>(
      `/projects/${projectId}/domains`,
    ),

  checkAvailability: (projectId: string, data: CheckAvailabilityPayload) =>
    api.post<{ results: DomainAvailabilityResult[] }>(
      `/projects/${projectId}/domains/check-availability`,
      data,
    ),

  purchase: (projectId: string, data: PurchaseDomainPayload) =>
    api.post<{ success: boolean; domainId?: string; error?: string }>(
      `/projects/${projectId}/domains/purchase`,
      data,
      longRequest('domainPurchase'),
    ),

  /**
   * WEB-1134 (QA 2026-08-13, дыра 3): детект DNS-провайдера домена по NS.
   * Сервер результат детекта НЕ хранит, поэтому окно «Инструкция по
   * подключению» перезапрашивает его при каждом открытии — иначе распознанная
   * ветка (A: панель умеет ALIAS на корне / B: нужна переадресация) теряется,
   * и человек снова читает общее «провайдера мы не распознали».
   * `provider: null` — штатный ответ «не распознан», не ошибка.
   */
  detectDnsProvider: (projectId: string, domain: string) =>
    api.get<{
      provider: import('@/components/domains/CnameSetupHelp').DnsProviderInfo | null;
    }>(`/projects/${projectId}/domains/dns-provider`, { params: { domain } }),

  /**
   * WEB-1135: Domain Connect — доступна ли автонастройка DNS «в один клик»
   * для домена. supported:false — кнопка не показывается, ручная инструкция
   * остаётся основным путём.
   */
  domainConnect: (
    projectId: string,
    params: { domain: string; host: string; target: string },
  ) =>
    api.get<{
      supported: boolean;
      providerDisplayName?: string;
      applyUrl?: string;
    }>(`/projects/${projectId}/domains/domain-connect`, { params }),

  /**
   * WEB-1134 атом 4: «Проверить сейчас» — настоящий немедленный re-check на
   * сервере (PATCH-сброс backoff у Cloudflare + свежий опрос), а не чтение
   * нашей же базы. Ответ несёт причины «почему не проходит» и TXT-записи.
   */
  verify: (projectId: string, domainId: string) =>
    api.post<{
      verificationStatus: string;
      verificationErrors?: string[];
      /**
       * WEB-1874 (копилка этапа 4): те же причины с машинным кодом рядом с
       * сырой строкой поставщика — кабинет показывает их на языке человека
       * (`components/domains/shared/DomainVerifyPanel.tsx`). Поле
       * необязательное: старый сервер его не присылает, и панель работает
       * прежним путём по `verificationErrors`.
       */
      errorDetails?: Array<{ code: string; raw: string }>;
      validationRecords?: Array<{ name: string; value: string }>;
      /**
       * WEB-1921: ответ НАШЕЙ проверки DNS — что реально стоит у регистратора.
       * Раньше кнопка отвечала только мнением Cloudflare, а оно обновляется не
       * мгновенно: клиент сносил CNAME, а кабинет часами показывал «Активен».
       * Поле необязательное: старый сервер его не присылает, панель тогда
       * работает как раньше.
       */
      dnsCheck?: DnsCheckResult;
    }>(`/projects/${projectId}/domains/${domainId}/verify`),

  /**
   * WEB-2489 (решение владельца 24.09 «научить») — перезапуск подключения
   * своего домена, остановленного по сроку: сервер заводит имя у Cloudflare
   * заново тем же путём, что при первом подключении, и переводит домен в
   * ожидание. `registered=false` — имя заведёт фон, когда CNAME начнёт вести
   * к нам. Организационный близнец — `restartConnectionOrg` ниже.
   */
  restartConnection: (projectId: string, domainId: string) =>
    api.post<RestartDomainConnectionResult>(
      `/projects/${projectId}/domains/${domainId}/restart-connection`,
    ),

  /** WEB-2489 — то же для домена на странице организации (Owner/Admin). */
  restartConnectionOrg: (orgId: string, domainId: string) =>
    api.post<RestartDomainConnectionResult>(
      `/organizations/${orgId}/domains/${domainId}/restart-connection`,
    ),

  /**
   * WEB-1921 — проверка записей ПОКАЗАННЫХ доменов на открытие раздела.
   * Cloudflare этой дверью не дёргается вовсе, состояние домена не меняется:
   * спрашиваем только DNS и показываем факт.
   */
  dnsCheck: (projectId: string, domainIds?: string[], fresh?: boolean) =>
    api.post<DnsCheckResponse>(`/projects/${projectId}/domains/dns-check`, {
      domainIds,
      fresh,
    }),

  /** WEB-1921 — то же для страницы доменов организации (`/org/domains`). */
  dnsCheckOrg: (orgId: string, domainIds?: string[], fresh?: boolean) =>
    api.post<DnsCheckResponse>(`/organizations/${orgId}/domains/dns-check`, {
      domainIds,
      fresh,
    }),

  connectCustom: (projectId: string, data: ConnectCustomDomainPayload) =>
    api.post<{
      success: boolean;
      domainId?: string;
      // CNAME target the user must point their registrar at (CF for SaaS). P5 §F2.
      cnameTarget?: string;
      error?: string;
      // WEB-1134: приходят только для корневого домена (и только от нового бэка)
      wwwDomainId?: string;
      dnsProvider?: import('@/components/domains/CnameSetupHelp').DnsProviderInfo | null;
      /**
       * WEB-2132 (атом 4, находка 1): backend уже присылает TXT-запись
       * подтверждения владения (`ConnectCustomDomainResponseDto`, атом 1) —
       * этих двух полей в типе не было, и кабинет их молча терял: инструкция
       * показывала только CNAME, а без TXT домен не выходит из «Ожидания».
       * Вызов ИДЕМПОТЕНТЕН, пока владение не подтверждено (см.
       * `connectSingleCustomDomain`/`connectApexDomain`, критерий Б3): тот же
       * домен той же организацией — тот же код, без повторной регистрации и
       * без обращения к Cloudflare, поэтому его можно звать повторно, чтобы
       * заново получить TXT для уже созданной строки.
       */
      txtRecordName?: string;
      txtRecordValue?: string;
      /**
       * WEB-2197 (п.1, Ж3): когда удалим неподтверждённую заявку — ГОТОВАЯ
       * дата сервера (`createdAt + OWNERSHIP_UNCONFIRMED_CLEANUP_DAYS`).
       * Считать срок на витрине запрещено: это вторая копия константы
       * (критерий Е5).
       */
      ownershipDeletionDeadlineAt?: string | null;
    }>(`/projects/${projectId}/domains/connect-custom`, data),

  /**
   * WEB-2132 (атом 4) — «Проверить сейчас» подтверждение владения доменом
   * через TXT (Ж4). Ручка НЕ бросает исключение, когда проверка не проходит
   * (В2/В3/В5 — сеть отдельно от «записи нет»), и НЕ меняет состояние домена
   * при сбое сети (шрам WEB-1921) — ответ содержит машинный `code` из реестра
   * `human-error-codes.ts`, переводить ТОЛЬКО по нему (`errorPhraseByCode`),
   * не по `message` (находка 2: `message` — готовая русская фраза с сервера).
   * WEB-2197 (п.3): у доменов ПУЛА организации теперь есть своя дверь —
   * `verifyOwnershipOrg` ниже. Та же форма ответа, та же проверка на сервере;
   * различается только чем судится право (проект против организации).
   */
  verifyOwnership: (projectId: string, domainId: string) =>
    api.post<{
      success: boolean;
      outcome: string;
      code?: string;
      /**
       * WEB-2270 (а): значения TXT, что сейчас стоят у имени записи, — только при
       * `OWNERSHIP_TXT_VALUE_MISMATCH`, до 5 по 200 знаков (усекает сервер).
       */
      txtRecordsFound?: string[];
      ownershipVerifiedAt?: string;
      /** WEB-2197 (п.1): когда удалим неподтверждённую заявку (дата сервера). */
      ownershipDeletionDeadlineAt?: string | null;
    }>(`/projects/${projectId}/domains/${domainId}/verify-ownership`),

  /**
   * WEB-2197 (п.3) — «Проверить владение» для домена пула ОРГАНИЗАЦИИ
   * (у него нет проекта, и проектная ручка ему недоступна по построению).
   * Отказы: 403 — спрашивающий не участник организации, 404 — домена нет
   * или он чужой.
   */
  verifyOwnershipOrg: (orgId: string, domainId: string) =>
    api.post<{
      success: boolean;
      outcome: string;
      code?: string;
      /** WEB-2270 (а): как у проектной ручки — только при несовпадении значения. */
      txtRecordsFound?: string[];
      ownershipVerifiedAt?: string;
      ownershipDeletionDeadlineAt?: string | null;
    }>(`/organizations/${orgId}/domains/${domainId}/verify-ownership`),

  /**
   * WEB-2132 (фикс-пачка 4, находка 2) — ЧИТАЮЩАЯ ручка TXT-записи владения.
   *
   * Раньше окно «Инструкция по подключению» добывало эту запись повторным
   * `connectCustom`, а тот стоит под орг-потолком подключения (10/час,
   * 30/сутки на организацию, считает ЗАПРОСЫ): десять открытий окна, пока
   * человек правит DNS, — и организация получала 429 на любое новое
   * подключение. Эта ручка потолок подключения не занимает и ничего не
   * меняет; отказы — 404 (нет домена), 403 (чужой проект), 400
   * `OWNERSHIP_VERIFICATION_NOT_APPLICABLE` (не CUSTOM / владение уже
   * подтверждено / кода нет).
   */
  ownershipTxt: (projectId: string, domainId: string) =>
    api.get<{
      success: boolean;
      txtRecordName: string;
      txtRecordValue: string;
    }>(`/projects/${projectId}/domains/${domainId}/ownership-txt`),

  /**
   * WEB-2206 — та же читающая ручка для домена ПУЛА ОРГАНИЗАЦИИ: у него нет
   * проекта, и проектная дверь ему недоступна по построению. Страница
   * «Домены организации» показывала «владение не подтверждено», а САМУ
   * запись показать было нечем — за ней приходилось возвращаться в окно
   * подключения.
   *
   * Отказы: 403 — спрашивающий не Owner/Admin организации (та же дверь, что
   * у `verifyOwnershipOrg`), 404 — домена нет или он чужой, 400
   * `OWNERSHIP_VERIFICATION_NOT_APPLICABLE` (не CUSTOM / уже подтверждён /
   * кода нет). Ничего не меняет и потолок подключений не занимает.
   */
  ownershipTxtOrg: (orgId: string, domainId: string) =>
    api.get<{
      success: boolean;
      txtRecordName: string;
      txtRecordValue: string;
    }>(`/organizations/${orgId}/domains/${domainId}/ownership-txt`),

  renew: (projectId: string, domainId: string) =>
    api.post<RenewDomainApiResponse>(
      `/projects/${projectId}/domains/${domainId}/renew`,
      undefined,
      longRequest('domainRenew'),
    ),

  /**
   * WEB-1636 — ОСОЗНАННЫЙ повтор продления НОВЫМ списанием.
   *
   * Отдельный маршрут, а не флаг у `renew`: обычный путь обязан и дальше
   * отвечать «повтор» и денег не трогать. Подтверждение обязательно и в теле
   * — сервер отбивает пустой POST до контроллера, то есть до денег.
   */
  forceRenew: (projectId: string, domainId: string) =>
    api.post<ForceRenewDomainApiResponse>(
      `/projects/${projectId}/domains/${domainId}/renew/force`,
      { confirmNewCharge: true },
      longRequest('domainForceRenew'),
    ),

  toggleAutoRenewal: (
    projectId: string,
    domainId: string,
    data: ToggleAutoRenewalPayload,
  ) =>
    api.post<ToggleAutoRenewalApiResponse>(
      `/projects/${projectId}/domains/${domainId}/auto-renewal`,
      data,
    ),

  renewBulk: (projectId: string, data: RenewBulkPayload) =>
    api.post<RenewBulkResponse>(
      `/projects/${projectId}/domains/renew-bulk`,
      data,
      longRequest('domainRenewBulk'),
    ),

  /**
   * WEB-3821 (деньги) — «Продлить» домена ПУЛА организации (без проекта).
   * Та же форма ответа, что у `renew`; право — владелец или администратор
   * организации (403 `FORBIDDEN`), заморозка — 403 `ORG_FROZEN`, домен с
   * проектом сюда не идёт («Domain not found»: у него ручка проекта).
   */
  renewOrg: (orgId: string, domainId: string) =>
    api.post<RenewDomainApiResponse>(
      `/organizations/${orgId}/domains/${domainId}/renew`,
      undefined,
      longRequest('orgDomainRenew'),
    ),

  /** WEB-3821 — осознанный повтор продления домена пула (WEB-1636): подтверждение в теле. */
  forceRenewOrg: (orgId: string, domainId: string) =>
    api.post<ForceRenewDomainApiResponse>(
      `/organizations/${orgId}/domains/${domainId}/renew/force`,
      { confirmNewCharge: true },
      longRequest('orgDomainForceRenew'),
    ),

  /** WEB-3821 — продление нескольких доменов пула организации; ответ — корзины, как у `renewBulk`. */
  renewBulkOrg: (orgId: string, data: RenewBulkPayload) =>
    api.post<RenewBulkResponse>(
      `/organizations/${orgId}/domains/renew-bulk`,
      data,
      longRequest('orgDomainRenewBulk'),
    ),

  /** WEB-3821 — автопродление домена пула организации; ответ несёт записанное значение. */
  toggleAutoRenewalOrg: (
    orgId: string,
    domainId: string,
    data: ToggleAutoRenewalPayload,
  ) =>
    api.post<ToggleAutoRenewalApiResponse>(
      `/organizations/${orgId}/domains/${domainId}/auto-renewal`,
      data,
    ),

  remove: (projectId: string, domainId: string) =>
    api.delete<{ success: boolean; error?: string }>(
      `/projects/${projectId}/domains/${domainId}`,
    ),

  /** WEB-390 — org-level domain list: GET /organizations/:orgId/domains */
  listOrg: (orgId: string) =>
    api.get<OrgDomainsListResponse>(`/organizations/${orgId}/domains`),

  /** WEB-730 — org-scoped removal of a CUSTOM domain (works for unassigned
   * org-pool domains, где project-scoped remove неприменим).
   * DELETE /organizations/:orgId/domains/:domainId */
  removeOrg: (orgId: string, domainId: string) =>
    api.delete<{ success: boolean; error?: string }>(
      `/organizations/${orgId}/domains/${domainId}`,
    ),

  /** WEB-558 — org-domains v2: assign / reassign / revoke (newProjectId: null)
   * a domain from the org-level table. POST /organizations/:orgId/domains/:domainId/reassign */
  reassignOrg: (
    orgId: string,
    domainId: string,
    data: { newProjectId: string | null },
  ) =>
    api.post<{ success: boolean; domain?: Domain }>(
      `/organizations/${orgId}/domains/${domainId}/reassign`,
      data,
    ),

  /**
   * WEB-559 — org-domains v2 "Buy domain" / "Connect domain" wizards need an
   * org-scoped (not project-scoped) availability/purchase/connect surface so a
   * domain can land unassigned in the org pool, per the design mockup
   * (org-domains-mockup/src/domains/BuyDomainModal.tsx + ConnectDomainModal.tsx).
   *
   * BACKEND-статус (WEB-1367, сверено 2026-08-16): ВСЕ ТРИ org-ручки
   * реализованы — connect-custom (WEB-606), check-availability и purchase
   * (org-domains.controller.ts:48-101, WEB-559/WEB-737). Прежняя пометка
   * «ЕЩЁ НЕТ … 404» устарела ещё в июле и снята.
   */
  checkOrgAvailability: (orgId: string, data: CheckAvailabilityPayload) =>
    api.post<{ results: DomainAvailabilityResult[] }>(
      `/organizations/${orgId}/domains/check-availability`,
      data,
    ),

  // WEB-1367: помимо мягкого отказа (success:false + error) сервер умеет и
  // ЖЁСТКИЙ отказ HTTP 503, когда регистратор недоступен
  // (org-domains.controller.ts:82-89) — он прилетает reject'ом промиса, окно
  // покупки его уже обрабатывает (OrgBuyDomainModal catch-ветка).
  buyOrgDomain: (orgId: string, data: PurchaseDomainPayload) =>
    api.post<{ success: boolean; domainId?: string; error?: string }>(
      `/organizations/${orgId}/domains/purchase`,
      data,
      longRequest('orgDomainPurchase'),
    ),

  connectOrgCustomDomain: (orgId: string, data: ConnectCustomDomainPayload) =>
    api.post<{
      success: boolean;
      domainId?: string;
      cnameTarget?: string;
      error?: string;
      // WEB-1134: приходят только для корневого домена (и только от нового бэка)
      wwwDomainId?: string;
      dnsProvider?: import('@/components/domains/CnameSetupHelp').DnsProviderInfo | null;
      /** WEB-2132 (атом 4, находка 1) — см. connectCustom выше, тот же ответ. */
      txtRecordName?: string;
      txtRecordValue?: string;
      /** WEB-2197 (п.1, Ж3) — см. connectCustom выше, та же дата сервера. */
      ownershipDeletionDeadlineAt?: string | null;
    }>(`/organizations/${orgId}/domains/connect-custom`, data),

  /**
   * WEB-1391: org-scoped Domain Connect discovery — org-flow эквивалент
   * `domainConnect` выше. Ручка `domainConnect` (GET
   * /projects/:projectId/domains/domain-connect) стоит за CapabilityGuard,
   * который резолвит :projectId из URL; orgId на его месте гард в реальной
   * среде отклонит. `org-domains.controller.ts` завёл собственный
   * `GET /organizations/:orgId/domains/domain-connect` (без CapabilityGuard —
   * этот контроллер везде держит RBAC на уровне сервиса, не гарда), поэтому
   * орг-окно (OrgConnectDomainModal) ходит сюда, а не в проектную ручку.
   */
  domainConnectOrg: (
    orgId: string,
    params: { domain: string; host: string; target: string },
  ) =>
    api.get<{
      supported: boolean;
      providerDisplayName?: string;
      applyUrl?: string;
    }>(`/organizations/${orgId}/domains/domain-connect`, { params }),
};
